import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { loginUnified, registerMember } from "../api/axios";
import { formatPhone } from "../utils/phone";

const EyeIcon = ({ off }) => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    {off ? (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" />
    ) : (
      <>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </>
    )}
  </svg>
);

const inputCls =
  "w-full h-[50px] border border-gray-300 rounded-xl px-4 focus:ring-2 focus:ring-[#00342b] focus:border-[#00342b] outline-none transition text-sm bg-white";

export default function AuthModal({ open, initialMode = "login", onClose, onLogin }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState(initialMode);
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", confirm: "" });

  useEffect(() => {
    if (open) {
      setMode(initialMode);
      setForm({ name: "", email: "", phone: "", password: "", confirm: "" });
      setShowPw(false);
    }
  }, [open, initialMode]);

  if (!open) return null;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const finishLogin = (user, token) => {
    onLogin(user, token);
    onClose();
    if (user?.role === "admin" || user?.role === "super_admin") {
      navigate("/admin/dashboard", { replace: true });
    } else {
      navigate("/account", { replace: true });
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await loginUnified(form.email.trim(), form.password);
      if (res?.success && res.token) {
        toast.success(`Welcome back, ${res.user?.name || "there"}!`);
        finishLogin(res.user, res.token);
      } else {
        toast.error(res?.message || "Invalid email or password");
      }
    } catch (err) {
      toast.error(err.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (form.password !== form.confirm) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      const res = await registerMember({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        password: form.password,
      });
      if (res?.success && res.token) {
        toast.success("Account created — welcome!");
        finishLogin(res.user, res.token);
      } else {
        toast.error(res?.message || "Could not create account");
      }
    } catch (err) {
      toast.error(err.message || "Registration failed. The email may already be registered.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden my-8">
        {/* Header */}
        <div className="bg-[#00342b] px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/10 rounded-xl h-10 w-10 flex items-center justify-center text-white font-bold text-lg">N</div>
            <div className="leading-tight">
              <p className="text-[10px] tracking-[0.28em] font-semibold text-white/60 uppercase">Network of</p>
              <p className="text-sm font-extrabold text-white uppercase">Muslim Physicians</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-white/70 hover:text-white text-3xl leading-none">×</button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100">
          {["login", "signup"].map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`flex-1 py-4 text-sm font-bold transition ${mode === m ? "text-[#00342b] border-b-2 border-[#00342b]" : "text-gray-400 hover:text-gray-600"}`}
            >
              {m === "login" ? "Sign In" : "Create Account"}
            </button>
          ))}
        </div>

        <div className="p-8">
          {mode === "login" ? (
            <form onSubmit={handleLogin} className="space-y-4" autoComplete="off">
              <p className="text-sm text-gray-500 -mt-2 mb-2">Sign in to your account — members and admins.</p>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email Address</label>
                <input
                  type="email"
                  name="login_id"
                  required
                  autoComplete="off"
                  readOnly
                  onFocus={(e) => e.target.removeAttribute("readonly")}
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder="you@example.com"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Password</label>
                <div className="relative">
                  <input
                    type={showPw ? "text" : "password"}
                    name="login_secret"
                    required
                    autoComplete="new-password"
                    readOnly
                    onFocus={(e) => e.target.removeAttribute("readonly")}
                    value={form.password}
                    onChange={(e) => set("password", e.target.value)}
                    placeholder="••••••••"
                    className={`${inputCls} pr-12`}
                  />
                  <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><EyeIcon off={showPw} /></button>
                </div>
              </div>
              <button type="submit" disabled={loading} className="w-full h-[50px] bg-[#00342b] text-white rounded-xl font-bold hover:bg-[#00493c] transition disabled:opacity-60 flex items-center justify-center gap-2">
                {loading && <Spinner />}
                {loading ? "Signing in..." : "Sign In"}
              </button>
              <p className="text-center text-sm text-gray-500">
                New here?{" "}
                <button type="button" onClick={() => setMode("signup")} className="text-[#00342b] font-semibold hover:underline">Create an account</button>
              </p>
            </form>
          ) : (
            <form onSubmit={handleSignup} className="space-y-4">
              <p className="text-sm text-gray-500 -mt-2 mb-2">Create a member account to post in the forum, apply to jobs, and manage your profile.</p>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Full Name</label>
                <input type="text" required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Dr. Fatima Al-Hassan" className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email Address</label>
                <input type="email" required autoComplete="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Phone <span className="text-gray-400 font-normal">(optional)</span></label>
                <input type="tel" value={form.phone} onChange={(e) => set("phone", formatPhone(e.target.value))} placeholder="1-917-903-8922" className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Password</label>
                  <div className="relative">
                    <input type={showPw ? "text" : "password"} required autoComplete="new-password" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="Min 6 chars" className={`${inputCls} pr-11`} />
                    <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><EyeIcon off={showPw} /></button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Confirm</label>
                  <input type={showPw ? "text" : "password"} required value={form.confirm} onChange={(e) => set("confirm", e.target.value)} placeholder="Re-enter" className={inputCls} />
                </div>
              </div>
              <button type="submit" disabled={loading} className="w-full h-[50px] bg-[#00342b] text-white rounded-xl font-bold hover:bg-[#00493c] transition disabled:opacity-60 flex items-center justify-center gap-2">
                {loading && <Spinner />}
                {loading ? "Creating..." : "Create Account"}
              </button>
              <p className="text-center text-sm text-gray-500">
                Already have an account?{" "}
                <button type="button" onClick={() => setMode("login")} className="text-[#00342b] font-semibold hover:underline">Sign in</button>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
    </svg>
  );
}
